/**
 * MetricCard - Summary stat card with optional change indicator
 */

interface MetricCardProps {
  label: string
  value: string | number | null | undefined
  change?: number // Optional % change (shows green/red arrow)
  format?: 'currency' | 'percent' | 'number' | 'ratio'
  size?: 'sm' | 'md' | 'lg'
}

/**
 * Format a number as compact currency (e.g., "$1.2B", "$42.5M")
 */
function formatCurrency(value: number): string {
  const absValue = Math.abs(value)

  if (absValue >= 1_000_000_000_000) {
    return `$${(value / 1_000_000_000_000).toFixed(1)}T`
  }
  if (absValue >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(1)}B`
  }
  if (absValue >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`
  }
  if (absValue >= 1_000) {
    return `$${(value / 1_000).toFixed(1)}K`
  }
  return `$${value.toFixed(2)}`
}

/**
 * Format a decimal value as percentage (e.g., 0.552 -> "55.2%")
 */
function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

/**
 * Format a number with appropriate precision
 */
function formatNumber(value: number): string {
  if (Math.abs(value) >= 1_000_000) {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value)
  }
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Format a ratio value (e.g., P/E ratio)
 */
function formatRatio(value: number): string {
  return value.toFixed(2)
}

function formatValue(
  value: string | number | null | undefined,
  format: MetricCardProps['format']
): string {
  if (value === null || value === undefined) {
    return '--'
  }

  if (typeof value === 'string') {
    return value
  }

  switch (format) {
    case 'currency':
      return formatCurrency(value)
    case 'percent':
      return formatPercent(value)
    case 'ratio':
      return formatRatio(value)
    case 'number':
    default:
      return formatNumber(value)
  }
}

export function MetricCard({
  label,
  value,
  change,
  format = 'number',
  size = 'md',
}: MetricCardProps) {
  const sizeStyles = {
    sm: {
      container: 'p-3',
      label: 'text-xs',
      value: 'text-lg',
      change: 'text-xs',
    },
    md: {
      container: 'p-4',
      label: 'text-sm',
      value: 'text-2xl',
      change: 'text-sm',
    },
    lg: {
      container: 'p-5',
      label: 'text-base',
      value: 'text-3xl',
      change: 'text-sm',
    },
  }

  const styles = sizeStyles[size]
  const formattedValue = formatValue(value, format)

  const hasChange = change !== undefined && change !== null
  const isPositive = hasChange && change > 0
  const isNegative = hasChange && change < 0

  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 shadow-sm ${styles.container}`}
    >
      <p className={`${styles.label} text-gray-500 font-medium truncate`}>
        {label}
      </p>
      <div className="flex items-baseline gap-2 mt-1">
        <p className={`${styles.value} font-semibold text-gray-900`}>
          {formattedValue}
        </p>
        {hasChange && (
          <span
            className={`${styles.change} flex items-center font-medium ${
              isPositive
                ? 'text-green-600'
                : isNegative
                  ? 'text-red-600'
                  : 'text-gray-500'
            }`}
          >
            {isPositive ? (
              <svg
                className="w-4 h-4 mr-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 10l7-7m0 0l7 7m-7-7v18"
                />
              </svg>
            ) : isNegative ? (
              <svg
                className="w-4 h-4 mr-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            ) : null}
            {Math.abs(change).toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  )
}

export default MetricCard
